import { RefObject, useLayoutEffect, useState } from 'react';

export interface FilterRowLayout {
    /** The full tabs fit in the row; otherwise they become a dropdown. */
    fullTabs: boolean;
    /** The month period fits beside the tabs in its widest state; otherwise it gets its own line. */
    monthInline: boolean;
}

/**
 * Decides the message filter row layout from invisible copies of the full tabs and of the month
 * period in its widest (selected month) state. Using the widest state keeps the month period on
 * the same line while it changes, so switching to a month never moves the page.
 * Without ResizeObserver (tests, very old browsers) the full one-line layout is kept.
 */
export const useFilterRowLayout = (
    row: RefObject<HTMLElement>,
    fullTabs: RefObject<HTMLElement>,
    widestMonth: RefObject<HTMLElement>,
    gap: number,
    hasMonth: boolean,
): FilterRowLayout => {
    const [layout, setLayout] = useState<FilterRowLayout>({
        fullTabs: true,
        monthInline: true,
    });

    useLayoutEffect(() => {
        if (typeof ResizeObserver === 'undefined') return;
        const check = () => {
            if (!row.current || !fullTabs.current) return;
            const available = row.current.clientWidth;
            const tabsWidth = fullTabs.current.offsetWidth;
            const monthWidth = hasMonth
                ? widestMonth.current?.offsetWidth ?? 0
                : 0;
            const tabsFit = tabsWidth <= available;
            const next = {
                fullTabs: tabsFit,
                monthInline:
                    !hasMonth ||
                    (tabsFit && tabsWidth + gap + monthWidth <= available),
            };
            setLayout(current =>
                current.fullTabs === next.fullTabs &&
                current.monthInline === next.monthInline
                    ? current
                    : next,
            );
        };
        const observer = new ResizeObserver(check);
        [row, fullTabs, widestMonth].forEach(
            ref => ref.current && observer.observe(ref.current),
        );
        check();
        return () => observer.disconnect();
    }, [row, fullTabs, widestMonth, gap, hasMonth]);

    return layout;
};
